/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, warp-drive/no-legacy-request-patterns */
import { computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Donut from '@linc-technologies/linced-common/components/chart-donut';

// D3 imports
import { select } from 'd3-selection';
import { arc as d3Arc, line as d3Line } from 'd3-shape';

const radialDialPercentages = 'progress:reporting:radialDialPercentages';

export default class ChartDonutProgression extends Donut {
  // Services
  @service
  options;

  @service
  store;

  // Props
  // person takes in a person model to ascertain for which school this dial
  // is being generated for.
  //
  // required: true.
  person = null;

  // Progression dials are always rendered small (100x100) with their own
  // checkmark/percentage overlay; the base Donut's legend needs a 120px
  // min-width (see .chart .legend in linced-charts.scss) that doesn't fit
  // and squeezes the donut svg down to a sliver.
  showLegend = false;

  // Methods
  // showRadialPercentages checks if the school has disabled showing the
  // percentages inside a radial dial.
  @computed('isGradable', 'person.learner.schoolId')
  get showRadialPercentages() {
    if (this.isGradable) {
      // Don't show percentages for gradable subjects.
      return false;
    }
    if (!isEmpty(this.person)) {
      let schoolId = this.get('person.learner.schoolId');
      let schoolRecord = this.store.peekRecord('school', schoolId);
      let optionValue = this.options.schoolGet(
        schoolRecord,
        radialDialPercentages,
      );

      if (!isEmpty(optionValue)) {
        return optionValue;
      }
    }

    // Default behaviour is to always show the percentages.
    return true;
  }

  // draw creates a renderable graph.
  draw() {
    super.draw(...arguments);
    let plot = select(this.element.querySelector('svg'));
    let g = plot.select('g');
    let width = this.width;
    let height = this.height;
    let radius = Math.min(width, height) / 2;
    let arc = d3Arc();

    if (this.active) {
      g.selectAll('.active')
        .data([{}])
        .enter()
        .append('circle')
        .attr('class', 'active')
        .attr('cx', () => 0)
        .attr('cy', () => 0)
        .attr('r', () => radius - 5)
        .attr('stroke', '#5e87b0')
        .attr('stroke-width', 2)
        .attr('fill', 'none')
        .attr('stroke-dasharray', '3,3');
    }

    if (this.complete) {
      let innerRadius = radius - this.thickness - 2;

      // Fill the donut
      g.selectAll('.complete')
        .data([{}])
        .enter()
        .append('circle')
        .attr('class', 'complete')
        .attr('cx', () => 0)
        .attr('cy', () => 0)
        .attr('r', () => innerRadius)
        .attr('fill', '#5e87b0');

      // Size and location of the tick
      let size = innerRadius * 1.2;
      let x = -1 * (size / 2);
      let y = -1 * (size / 2);

      // Data which can be plotted to draw a tick
      let coordinates = [
        { x: x + size / 8, y: y + size / 2.4 },
        { x: x + size / 2.2, y: y + size - size / 4 },
        { x: x + size - size / 8, y: y + size / 10 },
      ];

      // Draw the tick
      let line = d3Line()
        .x(function (d) {
          return d.x;
        })
        .y(function (d) {
          return d.y;
        });

      g.append('path')
        .attr('d', line(coordinates))
        .attr('fill', 'none')
        .attr('stroke-width', 5)
        .attr('stroke', '#ffffff');
    } else {
      // Generate content in the middle, if applicable.
      let developing = 0;
      let secure = 0;
      let total = 0;
      let smallRadius = radius - this.thickness - 2;

      this.data.forEach((row) => {
        total += row.count;

        switch (row.value) {
          case 'developing':
            developing = row.count;
            break;
          case 'secure':
            secure = row.count;
            break;
        }
      });

      if (this.showRadialPercentages && (developing || secure)) {
        // Secure
        g.append('path')
          .attr(
            'd',
            arc({
              innerRadius: 0,
              outerRadius: smallRadius,
              startAngle: -Math.PI * 0.5,
              endAngle: Math.PI * 0.5,
            }),
          )
          .attr('fill', '#66bb6a')
          .attr('stroke-width', 2)
          .attr('stroke', '#ffffff');

        g.append('text')
          .attr('class', 'secure-percentage')
          .attr('x', 0)
          .attr('y', -6)
          .text(Math.round((secure / total) * 100) + '%');

        // Developing
        g.append('path')
          .attr(
            'd',
            arc({
              innerRadius: 0,
              outerRadius: smallRadius,
              startAngle: Math.PI * 0.5,
              endAngle: Math.PI * 1.5,
            }),
          )
          .attr('fill', '#ffecb3')
          .attr('stroke-width', 2)
          .attr('stroke', '#ffffff');

        g.append('text')
          .attr('class', 'developing-percentage')
          .attr('x', 0)
          .attr('y', 12)
          .text(Math.round((developing / total) * 100) + '%');
      }
    }
  }
}
